import { Container } from 'ui';
import { Trans, useTranslation } from 'react-i18next';
import { useState } from 'react';
import TripDocuments from './TripDocuments';
import TripChat from './TripChat';
import {
  closeTripConversations,
  getTripConversations,
  saveTripDays,
} from 'api/trip';
import { useAsync } from 'react-async';
import TripConversationItem from './TripConversationItem';
import { KoobButton, KoobCard, KoobModal } from '@koob/ui';
import { Modal } from '../../../components';
import { useApp, useError, useSnack } from '../../../hooks';
import { isEqual } from 'lodash';
import { SpinnerContainer } from 'containers/Hotels/HotelsDetail';
import { Spinner as Loader, Stack } from '@koob/margaret';
import ConversationForm from '../../../components/Forms/ConversationForm';
import { createTripConversations } from '../../../api/trip';
import { createFolder } from 'api/folders';
import { formatDate } from '@koob/dates';
import { decodeId } from 'utils';
import styled from 'styled-components';
import { ExtraScopeEnum, TripConversationStatusEnum } from '@koob/enums';
import { useDays, useTripFilters } from '@koob/trip-designer';
import { useKoobStore } from 'store';

const Warning = styled.div`
  color: ${({ theme }) => theme.danger};
  font-weight: bold;
`;

// initialQuestion (KD-3848): a question the factsheet assistant could not
// answer; the new-conversation form opens with it as the title.
const ComposeChatTrip = ({ tripId, reloadTrip, trip, initialQuestion }) => {
  const { t } = useTranslation('tripDesigner');
  const [showTab, setShowTab] = useState('Chat');
  const [selectedConversation, setSelectedConversation] = useState();
  const [showConversationTab, setShowConversationTab] = useState('');
  const tripFilters = useTripFilters();
  const [sortList, setSortList] = useState(true);
  const [showConversationModal, setShowConversationModal] = useState(
    Boolean(initialQuestion),
  );
  const [closeConversation, setCloseConversation] = useState(false);
  const { sendErrorSnack } = useError();
  const { notify } = useSnack();
  const { user } = useApp();
  const refreshFolder = useKoobStore(state => state.refreshFolder);
  const days = useDays();

  const requestFilters = {
    status: tripFilters?.conversationStatus?.displayName ?? undefined,
    order: 'alphabetical',
    direction: sortList ? 'ASC' : 'DESC',
  };

  const {
    data: conversations,
    reload,
    isLoading,
  } = useAsync({
    promiseFn: getTripConversations,
    tripId: tripId ?? tripFilters?.tripId,
    filters: requestFilters,
    watchFn: (props, prevProps) => {
      return !isEqual(props.filters, prevProps.filters);
    },
  });

  const createClientFolder = async () => {
    if (tripFilters?.clientFolder) {
      return;
    }

    const folder = (
      await createFolder({
        name: user.lastName,
        travelers: [],
      })
    ).data;

    if (tripFilters) {
      tripFilters.clientFolder = folder;
    }

    return folder;
  };

  const saveTripDraft = async resolvedTripId => {
    const travelers = {
      adultPax: tripFilters?.composition?.adults,
      // Both the composition and its birthdates are optional: a trip hydrated
      // from a folder without children carries none, and outside the compose
      // screen there is no composition at all.
      childrenBirthdates:
        tripFilters?.composition?.childrenBirthdates?.map(item =>
          formatDate(item, 'YYYY-MM-DD'),
        ) ?? [],
    };

    const folder = trip?.clientFolder?.id
      ? undefined
      : await createClientFolder();

    await saveTripDays({
      tripId: resolvedTripId,
      tripDays:
        days?.map((day, index) => {
          return {
            dayIndex: index + 1,
            experienceId: day?.experienceId ?? undefined,
            hotelId: day?.hotelId ? '' + decodeId(day.hotelId) : undefined,
            hotelBookedOnMyOwn: day?.hotelBookedOnMyOwn ?? false,
            hotelSelectedRoomKey: day?.selectedRoom?.roomKey ?? undefined,
            roomsComposition: day?.roomsComposition ?? null,
            options:
              day?.selectedExtras
                ?.filter(extra => extra.scope === ExtraScopeEnum.DAY)
                ?.map(extra => ({
                  extraId: extra.id,
                  parentExtraId: extra.parentExtraId,
                  // KD-3650 — one line per occurrence: the key and the rank are
                  // what tells them apart once the trip is read back. Without
                  // them this save collapsed a split service back into a single
                  // occurrence.
                  occurrenceKey: extra.occurrenceKey ?? undefined,
                  rank: extra.rank ?? undefined,
                  parentOccurrenceKey: extra.parentOccurrenceKey ?? undefined,
                  adultCount: extra.adultCount ?? extra.adults ?? undefined,
                  childrenBirthdates: extra.childrenBirthdates ?? undefined,
                })) ?? [],
          };
        }) ?? [],
      folderId:
        tripFilters?.clientFolder?.id ?? trip?.clientFolder?.id ?? folder?.id,
      travelers: travelers,
      globalOptions:
        days?.flatMap((d, dayIdx) =>
          (d?.selectedExtras ?? [])
            .filter(extra => extra.scope === ExtraScopeEnum.GLOBAL)
            .map(extra => ({
              dayIndex: dayIdx + 1,
              extraId: extra.id,
              parentExtraId: extra.parentExtraId,
              occurrenceKey: extra.occurrenceKey ?? undefined,
              rank: extra.rank ?? undefined,
              parentOccurrenceKey: extra.parentOccurrenceKey ?? undefined,
              adultCount: extra.adultCount ?? extra.adults ?? undefined,
              childrenBirthdates: extra.childrenBirthdates ?? undefined,
            })),
        ) ?? [],
    });

    notify(t('compose.savedAsDraft'));
    reloadTrip();
    // Keep the sidebar active-folder card in sync after attaching the
    // trip/folder (US-016). No-op when no active folder.
    refreshFolder();
  };

  const createTripConversation = async values => {
    if (!values.title || values.title.trim() === '') return;

    const resolvedTripId = tripId ?? tripFilters?.tripId;

    let response;
    try {
      response = await createTripConversations({
        tripId: resolvedTripId,
        body: {
          title: values.title,
        },
      });
    } catch (error) {
      sendErrorSnack(error);
      return;
    }

    // The conversation exists from here on: open it and close the modal right
    // away so a failure of the draft save below can never be reported as a
    // failed creation — it used to leave the modal open with an error snack on
    // a conversation that had in fact been created.
    reload();
    setSelectedConversation(response?.data);
    setShowConversationTab('list');
    setShowConversationModal(false);

    // Saving the itinerary alongside the new conversation only makes sense on
    // the compose screen, where the store holds the trip being designed. On the
    // standalone trip chat page the store is empty or still holds another trip,
    // and PUT /days replaces the itinerary — saving there wiped or overwrote
    // the days of the trip being discussed.
    const designerTargetsThisTrip =
      Boolean(tripFilters?.tripId) &&
      String(tripFilters.tripId) === String(resolvedTripId);

    if (!designerTargetsThisTrip || !days?.length) {
      return;
    }

    try {
      await saveTripDraft(resolvedTripId);
    } catch (error) {
      sendErrorSnack(error);
    }
  };

  const handleCloseConversation = () => {
    closeTripConversations(tripId, selectedConversation.id).then(() => {
      notify(t('conversation.closed'));
      reload();
    });
    setCloseConversation(false);
    setSelectedConversation(undefined);
    setShowConversationTab('');
  };

  return (
    <Container>
      <div className="flex">
        <div className="w-1/4 p-2">
          <div className="flex justify-between h-full">
            <KoobCard style={{ width: '100%' }}>
              <div className="flex items-center justify-between text-center pb-2 border-b">
                <div>{t('conversation.title')}</div>
                <div className="flex items-center">
                  <div
                    className="flex items-center mr-2"
                    onClick={() => reload()}
                  >
                    <i className="far fa-rotate-right text-gray-600 p-2 rounded-lg text-lg" />
                  </div>
                  <div
                    className={`flex items-center ${
                      sortList ? 'text-gray-600' : 'bg-gray-150 text-orange-600'
                    } rounded-lg p-2 text-lg`}
                    onClick={() => setSortList(!sortList)}
                  >
                    <i className="far fa-arrow-down-z-a" />
                  </div>
                </div>
              </div>

              <div
                className={`w-full ${showConversationTab === 'list' ? 'h-[46rem]' : 'h-[38rem]'} overflow-auto`}
              >
                {conversations?.data?.length === 0 && (
                  <div className="text-center text-gray-500 py-4">
                    <p className="text-xl font-bold uppercase">
                      {t('conversation.emptyMessage')}
                    </p>
                    <p className="text-sm">
                      {t('conversation.emptyMessageDescription')}
                    </p>
                  </div>
                )}
                {isLoading && (
                  <SpinnerContainer>
                    <Loader />
                  </SpinnerContainer>
                )}
                {(conversations?.data || []).map((item, index) => (
                  <TripConversationItem
                    conversation={item}
                    tripId={tripId}
                    key={index}
                    setSelectedConversation={setSelectedConversation}
                    selectedConversation={selectedConversation}
                    setShowConversationTab={setShowConversationTab}
                  />
                ))}
              </div>
              <KoobButton
                variant="primary"
                onClick={() => {
                  setShowConversationModal(true);
                }}
              >
                <i className="fa-light fa-circle-plus" />
                {t('conversation.new')}
              </KoobButton>
            </KoobCard>
          </div>
        </div>

        <KoobCard
          style={{ width: '100%', marginTop: '0.5rem', marginBottom: '0.5rem' }}
        >
          <div className="flex justify-between">
            <div className="flex items-center">
              {[
                ...(showConversationTab === 'list'
                  ? [
                      {
                        label: t('conversation.documents.title'),
                        href: `documents`,
                      },
                    ]
                  : []),
                {
                  label: t('conversation.chat.title'),
                  href: `chat`,
                },
              ].map(
                link =>
                  showConversationTab === 'list' && (
                    <div
                      key={link.label}
                      onClick={() => {
                        setShowTab(link.label);
                      }}
                      className={`inline-flex items-center px-1 py-1 text-sm font-medium text-gray-900 ${
                        showTab === link.label
                          ? 'border-b-4 border-orange-500'
                          : ''
                      }`}
                    >
                      {link.label}
                    </div>
                  ),
              )}
            </div>
            <div className="flex items-center">
              {selectedConversation?.id &&
                selectedConversation?.status !==
                  TripConversationStatusEnum.CLOSED && (
                  <button
                    disabled={
                      selectedConversation?.status ===
                      TripConversationStatusEnum.CLOSED
                    }
                    className={`${
                      selectedConversation?.status ===
                      TripConversationStatusEnum.CLOSED
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-orange-400 text-white hover:bg-orange-600'
                    }
                    px-4 py-1 rounded-xl mb-2
                  `}
                    onClick={() => setCloseConversation(true)}
                  >
                    {t('conversation.closeConversation')}
                  </button>
                )}
            </div>
          </div>

          {showTab === 'Documents' && (
            <TripDocuments
              tripId={tripId}
              conversationId={selectedConversation?.id}
            />
          )}
          {showTab === 'Chat' && (
            <TripChat
              reload={reload}
              tripId={tripId}
              conversationId={selectedConversation?.id}
              conversationTitle={selectedConversation?.title}
              conversationStatus={selectedConversation?.status}
              showConversationTab={showConversationTab}
            />
          )}
        </KoobCard>
        {showConversationModal && (
          <KoobModal
            open={showConversationModal}
            setOpen={setShowConversationModal}
            overflow="visible"
          >
            <div className="px-5">
              <ConversationForm
                onSubmit={values => createTripConversation(values)}
                setShowConversationModal={setShowConversationModal}
                initialTitle={initialQuestion}
              />
            </div>
          </KoobModal>
        )}

        {closeConversation && (
          <Modal
            isOpen
            title={t('conversation.close.title', {
              name: selectedConversation?.title,
            })}
            onRequestClose={() => setCloseConversation(false)}
            footer={
              <Stack alignX="flex-end" gutterSize={1}>
                <KoobButton onClick={handleCloseConversation} variant="danger">
                  <i
                    className="fa-regular fa-box-archive"
                    style={{ fontSize: 20 }}
                  />
                  {t('conversation.close.confirm')}
                </KoobButton>
                <KoobButton
                  variant="secondary"
                  onClick={() => setCloseConversation(false)}
                >
                  {t('misc:cancel')}
                </KoobButton>
              </Stack>
            }
          >
            <Stack direction="column" gutterSize={1} alignX="center">
              {selectedConversation?.id && (
                <>
                  <Warning>{t(`conversation.close.heading`)}</Warning>
                  <div>
                    <Trans
                      i18nKey="conversation.close.explanation"
                      name={selectedConversation.title}
                    >
                      <strong>{selectedConversation.title}</strong>
                      {t('conversation.close.explanationTexte')}
                    </Trans>
                  </div>
                </>
              )}
            </Stack>
          </Modal>
        )}
      </div>
    </Container>
  );
};

export default ComposeChatTrip;
